import { useState } from "react";
import { CalendarBlank, X } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import {
  startOfMonth, endOfMonth, startOfYear, subMonths, subDays, format,
} from "date-fns";
import { it, enGB, fr, de, es } from "date-fns/locale";
import { useI18n } from "../lib/i18n";

const DATE_FNS_LOCALES = { it, en: enGB, fr, de, es };

export function DateRangeFilter({ range, onChange }) {
  const { t, lang } = useI18n();
  const dfLocale = DATE_FNS_LOCALES[lang] || it;
  const PRESETS = [
    { key: "30d", label: t("dr_preset_30d"), range: () => ({ from: subDays(new Date(), 30), to: new Date() }) },
    { key: "this_month", label: t("dr_preset_this_month"), range: () => ({ from: startOfMonth(new Date()), to: new Date() }) },
    { key: "last_month", label: t("dr_preset_last_month"), range: () => ({ from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) }) },
    { key: "this_year", label: t("dr_preset_this_year"), range: () => ({ from: startOfYear(new Date()), to: new Date() }) },
  ];
  const [activePreset, setActivePreset] = useState(null);
  const [open, setOpen] = useState(false);

  const applyPreset = (p) => {
    setActivePreset(p.key);
    onChange(p.range());
    setOpen(false);
  };

  const clear = () => {
    setActivePreset(null);
    onChange({ from: undefined, to: undefined });
  };

  const label = range?.from
    ? `${format(range.from, "d MMM", { locale: dfLocale })} - ${range.to ? format(range.to, "d MMM yyyy", { locale: dfLocale }) : "…"}`
    : t("dr_all_period");

  return (
    <div className="tf-card p-4 flex flex-wrap items-center gap-2" data-testid="date-range-filter">
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => applyPreset(p)}
            data-testid={`preset-${p.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              activePreset === p.key
                ? "bg-[var(--tf-ink)] text-white"
                : "bg-[var(--tf-bg)] text-[var(--tf-muted)] hover:text-[var(--tf-ink)]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            onClick={() => setActivePreset(null)}
            className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border border-[var(--tf-border)] text-[var(--tf-ink)] hover:border-[var(--tf-primary)] transition-colors"
            data-testid="custom-range-trigger"
          >
            <CalendarBlank size={14} weight="bold" /> {label}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end" data-testid="custom-range-popover">
          <Calendar
            mode="range"
            selected={range}
            onSelect={(r) => onChange(r || { from: undefined, to: undefined })}
            numberOfMonths={2}
            locale={dfLocale}
          />
        </PopoverContent>
      </Popover>

      {(range?.from || range?.to) && (
        <button
          onClick={clear}
          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full text-xs text-[var(--tf-muted)] hover:text-[var(--tf-error)] transition-colors"
          data-testid="clear-range-btn"
        >
          <X size={13} weight="bold" /> {t("dr_clear_filter")}
        </button>
      )}
    </div>
  );
}
